import React, { useState, useEffect, useMemo } from "react";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
    BarChart, Bar, Cell,
} from "recharts";
import { api } from "../api/client";
import LoadingSpinner from "../components/LoadingSpinner";

const chartTooltipStyle = {
    backgroundColor: "#1e2130",
    border: "1px solid #2a2d3a",
    borderRadius: "8px",
    color: "#e8eaf0",
    fontSize: "13px",
};

const COLORS = [
    "#3b82f6", "#22c55e", "#f97316", "#ef4444", "#a855f7",
    "#ec4899", "#14b8a6", "#eab308", "#6366f1", "#f43f5e",
    "#06b6d4", "#84cc16", "#d946ef", "#fb923c", "#64748b",
];

function formatDuration(ms) {
    if (ms == null) return "-";
    if (ms < 1000) return ms + "ms";
    if (ms < 60000) return (ms / 1000).toFixed(1) + "s";
    return (ms / 60000).toFixed(1) + "m";
}

function formatNumber(n) {
    if (n == null) return "-";
    return n.toLocaleString();
}

function Workers() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [days, setDays] = useState(7);
    const [selectedFunctions, setSelectedFunctions] = useState([]);
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [metric, setMetric] = useState("run_count");
    const [viewMode, setViewMode] = useState("daily"); // daily or today

    useEffect(() => {
        setLoading(true);
        api.workers({ days })
            .then((d) => { setData(d); setError(null); })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, [days]);

    // Derive function list from summary
    const functionList = useMemo(() => {
        if (!data?.summary) return [];
        return data.summary.map(s => s.function_name).sort();
    }, [data]);

    // Category → function mapping
    const categoryFunctions = useMemo(() => {
        if (!data?.categories) return {};
        return data.categories;
    }, [data]);

    // Filtered functions based on category + selection
    const activeFunctions = useMemo(() => {
        let fns = functionList;
        if (categoryFilter !== "all" && categoryFunctions[categoryFilter]) {
            fns = fns.filter(f => categoryFunctions[categoryFilter].includes(f));
        }
        if (selectedFunctions.length > 0) {
            fns = fns.filter(f => selectedFunctions.includes(f));
        }
        return fns;
    }, [functionList, categoryFilter, selectedFunctions, categoryFunctions]);

    // Build daily chart data — pivot by day with one key per function
    const dailyChartData = useMemo(() => {
        if (!data?.daily) return [];
        const byDay = {};
        for (const row of data.daily) {
            if (!activeFunctions.includes(row.function_name)) continue;
            if (!byDay[row.day]) byDay[row.day] = { day: row.day };
            byDay[row.day][row.function_name] = row[metric] || 0;
        }
        return Object.values(byDay).sort((a, b) => a.day.localeCompare(b.day));
    }, [data, activeFunctions, metric]);

    // Build hourly chart data (today)
    const hourlyChartData = useMemo(() => {
        if (!data?.hourly) return [];
        const byHour = {};
        for (let h = 0; h < 24; h++) {
            byHour[h] = { hour: String(h).padStart(2, "0") + ":00" };
        }
        for (const row of data.hourly) {
            if (!activeFunctions.includes(row.function_name)) continue;
            const metricMap = {
                run_count: "run_count",
                total_duration_ms: "total_duration_ms",
                avg_duration_ms: "avg_duration_ms",
                total_items_processed: "total_items_processed",
            };
            byHour[row.hour][row.function_name] = row[metricMap[metric]] || 0;
        }
        return Object.values(byHour);
    }, [data, activeFunctions, metric]);

    // Summary stats filtered
    const filteredSummary = useMemo(() => {
        if (!data?.summary) return [];
        return data.summary.filter(s => activeFunctions.includes(s.function_name));
    }, [data, activeFunctions]);

    // Totals row
    const totals = useMemo(() => {
        if (!filteredSummary.length) return null;
        return filteredSummary.reduce((acc, s) => ({
            total_runs: acc.total_runs + s.total_runs,
            success_count: acc.success_count + s.success_count,
            error_count: acc.error_count + s.error_count,
            running_count: acc.running_count + s.running_count,
            total_items_processed: acc.total_items_processed + s.total_items_processed,
            total_items_failed: acc.total_items_failed + s.total_items_failed,
            total_duration_ms: acc.total_duration_ms + s.total_duration_ms,
        }), { total_runs: 0, success_count: 0, error_count: 0, running_count: 0, total_items_processed: 0, total_items_failed: 0, total_duration_ms: 0 });
    }, [filteredSummary]);

    // Estimate cost (rough ballpark based on Azure pricing)
    // Flex Consumption (512MB instances):
    //   - On-demand:      $0.000016/GB-s active execution
    //   - Per execution:  $0.20 per million ($0.0000002 each)
    //   - Always-ready:   ~$22/mo per 512MB instance baseline (1 instance default)
    // Container Apps Jobs: ~$0.000012/vCPU-s + ~$0.0000015/GiB-s
    const estimatedCost = useMemo(() => {
        if (!filteredSummary.length) return null;
        const lookbackDays = data?.lookback || days;
        let flexCost = 0;
        let containerCost = 0;
        const containerFns = categoryFunctions["Container Apps"] || [];

        for (const s of filteredSummary) {
            const durationSec = s.total_duration_ms / 1000;
            if (containerFns.includes(s.function_name)) {
                // Container Apps: 0.5 vCPU, 1 GiB per job
                const vcpuCost = durationSec * 0.5 * 0.000012;
                const memCost = durationSec * 1 * 0.0000015;
                containerCost += vcpuCost + memCost;
            } else {
                // Flex Consumption on-demand: 0.5 GB memory assumed
                const gbS = durationSec * 0.5;
                const execCost = s.total_runs * 0.0000002;
                flexCost += (gbS * 0.000016) + execCost;
            }
        }

        // Always-ready baseline: 1 instance @ 512MB ~ $22/mo flat
        // Only counted when filters include any Flex function (i.e. user is
        // viewing flex workers — otherwise it's a misleading add-on).
        const includesFlex = filteredSummary.some(s => !containerFns.includes(s.function_name));
        const alwaysReadyMonthlyPerInstance = 22;
        const alwaysReady = includesFlex
            ? (alwaysReadyMonthlyPerInstance / 30) * lookbackDays
            : 0;

        const total = flexCost + containerCost + alwaysReady;
        return { flex: flexCost, container: containerCost, alwaysReady, total };
    }, [filteredSummary, categoryFunctions, data, days]);

    const toggleFunction = (fn) => {
        setSelectedFunctions(prev =>
            prev.includes(fn) ? prev.filter(f => f !== fn) : [...prev, fn]
        );
    };

    if (loading) return <LoadingSpinner message="Loading worker data..." />;
    if (error) return <div className="empty-state"><h3>Error: {error}</h3></div>;

    const chartData = viewMode === "today" ? hourlyChartData : dailyChartData;
    const xKey = viewMode === "today" ? "hour" : "day";

    return (
        <div>
            <div className="page-header">
                <h2>Azure Workers</h2>
                <p>Flex Consumption Functions & Container Apps job execution metrics from WorkerRunLog</p>
            </div>

            {/* Filters */}
            <div className="filter-row" style={{ flexWrap: "wrap", gap: "8px" }}>
                <select className="filter-select" value={days} onChange={e => setDays(Number(e.target.value))}>
                    <option value={1}>Last 24h</option>
                    <option value={3}>Last 3 days</option>
                    <option value={7}>Last 7 days</option>
                    <option value={14}>Last 14 days</option>
                    <option value={30}>Last 30 days</option>
                </select>
                <select className="filter-select" value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setSelectedFunctions([]); }}>
                    <option value="all">All Workers</option>
                    {Object.keys(categoryFunctions).map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                    ))}
                </select>
                <select className="filter-select" value={metric} onChange={e => setMetric(e.target.value)}>
                    <option value="run_count">Run Count</option>
                    <option value="total_duration_ms">Total Duration (ms)</option>
                    <option value="avg_duration_ms">Avg Duration (ms)</option>
                    <option value="total_items_processed">Items Processed</option>
                </select>
                <select className="filter-select" value={viewMode} onChange={e => setViewMode(e.target.value)}>
                    <option value="daily">Daily</option>
                    <option value="today">Today (Hourly)</option>
                </select>
                {selectedFunctions.length > 0 && (
                    <button className="filter-select" style={{ cursor: "pointer", color: "#3b82f6" }} onClick={() => setSelectedFunctions([])}>
                        Clear selection ({selectedFunctions.length})
                    </button>
                )}
            </div>

            {/* Summary cards */}
            {totals && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "12px", marginBottom: "16px" }}>
                    <SummaryCard label="Total Runs" value={formatNumber(totals.total_runs)} />
                    <SummaryCard label="Success" value={formatNumber(totals.success_count)} color="#22c55e" />
                    <SummaryCard label="Errors" value={formatNumber(totals.error_count)} color="#ef4444" />
                    <SummaryCard label="Items Processed" value={formatNumber(totals.total_items_processed)} />
                    <SummaryCard label="Total Duration" value={formatDuration(totals.total_duration_ms)} />
                    {estimatedCost && (
                        <SummaryCard label="Est. Cost" value={"$" + estimatedCost.total.toFixed(2)} color="#f97316"
                            subtitle={`Flex $${estimatedCost.flex.toFixed(2)} + AR $${estimatedCost.alwaysReady.toFixed(2)} / CA $${estimatedCost.container.toFixed(2)}`} />
                    )}
                </div>
            )}

            {/* Line chart */}
            <div className="card" style={{ marginBottom: "16px" }}>
                <div className="card-title">
                    {viewMode === "today" ? "Today — Hourly" : `Daily Trend — Last ${days} days`}
                    <span style={{ fontSize: "12px", color: "#8b8fa3", marginLeft: "12px" }}>
                        {metric === "run_count" ? "Executions" :
                         metric === "total_duration_ms" ? "Total Duration (ms)" :
                         metric === "avg_duration_ms" ? "Avg Duration (ms)" : "Items Processed"}
                    </span>
                </div>
                {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                        <LineChart data={chartData}>
                            <CartesianGrid stroke="#2a2d3a" />
                            <XAxis dataKey={xKey} stroke="#8b8fa3" tick={{ fontSize: 11 }} />
                            <YAxis stroke="#8b8fa3" tick={{ fontSize: 11 }}
                                tickFormatter={v => metric.includes("duration") ? formatDuration(v) : formatNumber(v)} />
                            <Tooltip
                                contentStyle={chartTooltipStyle}
                                formatter={(val, name) => [
                                    metric.includes("duration") ? formatDuration(val) : formatNumber(val),
                                    name
                                ]}
                            />
                            <Legend wrapperStyle={{ fontSize: "11px" }} />
                            {activeFunctions.map((fn, i) => (
                                <Line key={fn} type="monotone" dataKey={fn}
                                    stroke={COLORS[i % COLORS.length]}
                                    strokeWidth={2} dot={false} connectNulls />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="empty-state"><p>No data for selected filters</p></div>
                )}
            </div>

            {/* Function selector chips */}
            <div className="card" style={{ marginBottom: "16px" }}>
                <div className="card-title">Filter by Function</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", padding: "4px 0" }}>
                    {functionList
                        .filter(fn => categoryFilter === "all" || (categoryFunctions[categoryFilter] || []).includes(fn))
                        .map((fn, i) => {
                            const isActive = selectedFunctions.length === 0 || selectedFunctions.includes(fn);
                            return (
                                <button key={fn} onClick={() => toggleFunction(fn)} style={{
                                    padding: "4px 10px", borderRadius: "12px", fontSize: "12px",
                                    border: `1px solid ${COLORS[i % COLORS.length]}`,
                                    background: isActive ? COLORS[i % COLORS.length] + "22" : "transparent",
                                    color: isActive ? COLORS[i % COLORS.length] : "#5c6078",
                                    cursor: "pointer", opacity: isActive ? 1 : 0.5,
                                    transition: "all 0.15s",
                                }}>
                                    {fn}
                                </button>
                            );
                        })}
                </div>
            </div>

            {/* Summary table */}
            <div className="card" style={{ marginBottom: "16px" }}>
                <div className="card-title">Function Summary — Last {days} days</div>
                <div style={{ overflowX: "auto" }}>
                    <table className="store-table">
                        <thead>
                            <tr>
                                <th style={{ textAlign: "left" }}>Function</th>
                                <th>Runs</th>
                                <th>Success</th>
                                <th>Errors</th>
                                <th>Items Processed</th>
                                <th>Items Failed</th>
                                <th>Total Duration</th>
                                <th>Avg Duration</th>
                                <th>Max Duration</th>
                                <th>Last Run (UTC)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredSummary.map((s) => (
                                <tr key={s.function_name} style={{ cursor: "pointer" }}
                                    onClick={() => toggleFunction(s.function_name)}>
                                    <td style={{ textAlign: "left", fontFamily: "monospace", fontSize: "12px" }}>
                                        {s.function_name}
                                        {(categoryFunctions["Container Apps"] || []).includes(s.function_name) && (
                                            <span style={{ marginLeft: "6px", fontSize: "10px", background: "#6366f122", color: "#6366f1", padding: "1px 6px", borderRadius: "8px" }}>CA</span>
                                        )}
                                    </td>
                                    <td>{formatNumber(s.total_runs)}</td>
                                    <td style={{ color: "#22c55e" }}>{formatNumber(s.success_count)}</td>
                                    <td style={{ color: s.error_count > 0 ? "#ef4444" : "#8b8fa3" }}>{formatNumber(s.error_count)}</td>
                                    <td>{formatNumber(s.total_items_processed)}</td>
                                    <td style={{ color: s.total_items_failed > 0 ? "#f97316" : "#8b8fa3" }}>{formatNumber(s.total_items_failed)}</td>
                                    <td>{formatDuration(s.total_duration_ms)}</td>
                                    <td>{formatDuration(s.avg_duration_ms)}</td>
                                    <td>{formatDuration(s.max_duration_ms)}</td>
                                    <td style={{ fontSize: "11px", color: "#8b8fa3" }}>
                                        {s.last_run ? new Date(s.last_run).toLocaleString() : "-"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Error breakdown bar chart */}
            {filteredSummary.some(s => s.error_count > 0) && (
                <div className="card" style={{ marginBottom: "16px" }}>
                    <div className="card-title">Error Distribution</div>
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={filteredSummary.filter(s => s.error_count > 0).sort((a, b) => b.error_count - a.error_count)}>
                            <CartesianGrid stroke="#2a2d3a" />
                            <XAxis dataKey="function_name" stroke="#8b8fa3" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} />
                            <YAxis stroke="#8b8fa3" tick={{ fontSize: 11 }} />
                            <Tooltip contentStyle={chartTooltipStyle} />
                            <Bar dataKey="error_count" name="Errors" radius={[4, 4, 0, 0]}>
                                {filteredSummary.filter(s => s.error_count > 0).map((_, i) => (
                                    <Cell key={i} fill="#ef4444" />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Recent errors table */}
            {data?.errors?.length > 0 && (
                <div className="card">
                    <div className="card-title">Recent Errors (last {days} days)</div>
                    <div style={{ overflowX: "auto" }}>
                        <table className="store-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: "left" }}>Function</th>
                                    <th>Time (UTC)</th>
                                    <th>Duration</th>
                                    <th style={{ textAlign: "left" }}>Error</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.errors
                                    .filter(e => activeFunctions.includes(e.function_name))
                                    .slice(0, 25)
                                    .map((e) => (
                                    <tr key={e.id}>
                                        <td style={{ textAlign: "left", fontFamily: "monospace", fontSize: "12px" }}>{e.function_name}</td>
                                        <td style={{ fontSize: "11px", whiteSpace: "nowrap" }}>
                                            {new Date(e.started_at).toLocaleString()}
                                        </td>
                                        <td>{formatDuration(e.duration_ms)}</td>
                                        <td style={{ textAlign: "left", fontSize: "12px", maxWidth: "400px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                            title={e.error_message}>
                                            {e.error_message || "-"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function SummaryCard({ label, value, color, subtitle }) {
    return (
        <div className="card" style={{ padding: "14px 16px", textAlign: "center" }}>
            <div style={{ fontSize: "11px", color: "#8b8fa3", marginBottom: "4px" }}>{label}</div>
            <div style={{ fontSize: "22px", fontWeight: 600, color: color || "#e8eaf0" }}>{value}</div>
            {subtitle && <div style={{ fontSize: "10px", color: "#5c6078", marginTop: "2px" }}>{subtitle}</div>}
        </div>
    );
}

export default Workers;
